<template>
  <div class="enterprise-dashboard">
    <!-- 左侧区域：上为数据统计，下为甘肃省地图 -->
    <div class="left-area">
      <!-- 数据统计 -->
      <div class="stat-cards">
        <div
          v-for="item in statCards"
          :key="item.key"
          class="stat-card"
          :style="{ '--accent': item.color }"
        >
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value">
            {{ item.value }}<span class="stat-unit">{{ item.unit }}</span>
          </div>
        </div>
      </div>

      <!-- 甘肃省地图 -->
      <div class="map-section">
        
      </div>
    </div>

    <!-- 右侧区域：未处理告警信息列表 -->
    <div class="right-area">
      <div class="info-panel">
        <div class="panel-header">
          <h3 class="panel-title">未处理告警信息</h3>
          <span class="panel-count">{{ alarmTotal }} 条</span>
        </div>
        <div class="panel-body">
          <div class="alarm-list">
            <div
              v-for="item in alarmList"
              :key="item.id"
              class="alarm-item"
            >
              <div class="alarm-item-head">
                <span class="alarm-type">{{ getAlarmType(item.type) }}</span>
                <span class="alarm-vehicle">{{ item.licenceNo || item.vin || '-' }}</span>
              </div>
              <div class="alarm-item-info">{{ item.info || '暂无告警信息' }}</div>
              <div class="alarm-item-foot">
                <span class="alarm-dept">{{ item.deptName || '-' }}</span>
                <span class="alarm-time">{{ item.occurTime || '-' }}</span>
                <a class="alarm-handle" @click="goAlarm(item)">去处理</a>
              </div>
            </div>
            <a-empty v-if="!alarmList.length" description="暂无未处理告警" class="alarm-empty" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import useUserStore from '@/store/modules/user'
import { getVehiclePageList } from '@/api/vehicle'
import { getSecurityUserPageList } from '@/api/securityUser/securityUser'
import { getTaskPageList } from '@/api/task/task.js'
import { getAlarmPageList } from '@/api/alarm'
import { getAlarmType } from '@/views/test/alarm/components/EnumUtils'

const store = useUserStore()
const router = useRouter()

const vehicleTotal = ref(0)
const securityTotal = ref(0)
const routeTotal = ref(0)
const alarmTotal = ref(0)
const alarmList = ref([])
const creditScore = ref(0)

const statCards = computed(() => [
  { key: 'vehicle', label: '已备案车辆总数', value: vehicleTotal.value, unit: '辆', color: '#1890ff' },
  { key: 'security', label: '已备案安全员总数', value: securityTotal.value, unit: '人', color: '#52c41a' },
  { key: 'route', label: '已申请路线总数', value: routeTotal.value, unit: '条', color: '#722ed1' },
  { key: 'alarm', label: '告警', value: alarmTotal.value, unit: '条', color: '#ff4d4f' },
  { key: 'credit', label: '企业信用总分', value: creditScore.value, unit: '分', color: '#fa8c16' },
])

// 已备案车辆总数
const loadVehicleTotal = async () => {
  try {
    const res = await getVehiclePageList({
      pageIndex: 1,
      pageSize: 1,
      conditionMap: { dept_id: store.deptId },
    })
    vehicleTotal.value = res?.data?.total || 0
  } catch (error) {
    console.error('获取已备案车辆总数失败:', error)
  }
}

// 已备案安全员总数
const loadSecurityTotal = async () => {
  try {
    const res = await getSecurityUserPageList({
      pageIndex: 1,
      pageSize: 1,
      conditionMap: { deptIdStr: store.deptId },
    })
    securityTotal.value = res?.data?.total || 0
  } catch (error) {
    console.error('获取已备案安全员总数失败:', error)
  }
}

// 已申请路线总数（以任务申请数统计）
const loadRouteTotal = async () => {
  try {
    const res = await getTaskPageList({ pageIndex: 1, pageSize: 1, conditionMap: {} })
    routeTotal.value = res?.data?.total || 0
  } catch (error) {
    console.error('获取已申请路线总数失败:', error)
  }
}

// 未处理告警列表（status = 0 未处理）
const loadAlarmList = async () => {
  try {
    const res = await getAlarmPageList({
      pageIndex: 1,
      pageSize: 50,
      conditionMap: { status: 0 },
    })
    alarmList.value = res?.data?.records || []
    alarmTotal.value = res?.data?.total || 0
  } catch (error) {
    console.error('获取未处理告警列表失败:', error)
    alarmList.value = []
  }
}

// 去处理：跳转到告警管理页面
const goAlarm = () => {
  router.push('/test/alarm')
}

onMounted(() => {
  loadVehicleTotal()
  loadSecurityTotal()
  loadRouteTotal()
  loadAlarmList()
})
</script> 

<style scoped lang="less">
.enterprise-dashboard {
  display: flex;
  gap: 16px;
  width: 100%;
  height: calc(100vh - 70px);
  padding: 16px;
  box-sizing: border-box;
  background-color: #f0f2f5;
  overflow: hidden;
}

/* 左侧区域 */
.left-area {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 数据统计卡片 */
.stat-cards {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  flex-shrink: 0;
}

.stat-card {
  position: relative;
  background: #ffffff;
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: var(--accent);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
  }
}

.stat-label {
  font-size: 14px;
  color: #68767e;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 32px;
  font-weight: bold;
  line-height: 1.1;
  color: var(--accent);
}

.stat-unit {
  font-size: 14px;
  font-weight: normal;
  color: #68767e;
  margin-left: 6px;
}

/* 地图区域 */
.map-section {
  flex: 1;
  min-height: 0;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);

  :deep(.gansu-map-wrapper) {
    min-height: 0;
  }
}

/* 右侧区域 */
.right-area {
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.info-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2329;
  position: relative;
  padding-left: 12px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 16px;
    border-radius: 2px;
    background: #ff4d4f;
  }
}

.panel-count {
  font-size: 13px;
  color: #ff4d4f;
  font-weight: 500;
}

.panel-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.alarm-list {
  height: 100%;
  overflow-y: auto;
  padding: 8px 12px;
}

.alarm-item {
  padding: 12px;
  border-radius: 8px;
  background: #fafafa;
  margin-bottom: 10px;
  border-left: 3px solid #ff4d4f;
  transition: background 0.2s ease;

  &:hover {
    background: #fff1f0;
  }
}

.alarm-item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.alarm-type {
  font-size: 13px;
  font-weight: 600;
  color: #ff4d4f;
}

.alarm-vehicle {
  font-size: 13px;
  color: #1f2329;
}

.alarm-item-info {
  font-size: 13px;
  color: #48505e;
  line-height: 1.5;
  margin-bottom: 8px;
  word-break: break-all;
}

.alarm-item-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #8c98a4;
}

.alarm-time {
  white-space: nowrap;
}

.alarm-handle {
  color: #1890ff;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: #ff4d4f;
  }
}

.alarm-empty {
  margin-top: 60px;
}
</style>
